import { ChangeEvent, SyntheticEvent, useContext, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { InputText } from "primereact/inputtext"
import { Dropdown, DropdownChangeParams } from "primereact/dropdown"
import { Checkbox, CheckboxChangeParams } from "primereact/checkbox"
import commonStyles from "../../common.module.css"
import { useConstants } from "../../../api/queries/useConstants"
import { ConstantsResponseData } from "../../../api/types/constants"
import { OrderLoading, UpdateOrderLoadingPaylod, UpdateOrderLoadingResponseData } from "../../../api/types/order"
import { Button } from "primereact/button"
import { useUpdateLoading } from "../../../api/queries/useUpdateLoading"
import { useOrderDetail } from "../../../api/queries/useOrderDetail"
import { validateLoading } from "./validations/loading"
import { validateEmail } from "./validations/common"
import FieldError, { FormErrors } from "../../FieldError"
import { classNames } from "primereact/utils"
import { OrderParams } from "."
import { isOrderEditable } from "../StateTag"
import AddressBook from "../../AddressBook"
import { AddressSimple } from "../../../api/types/address"
import { usePrompt } from "../../../common/usePrompt"
import { PhoneInput } from "../../common/PhoneInput"
import { splitNumber } from "../../common/PhoneInput/utils"
import userContext from "../../../context/user"
import styles from "./styles.module.css"
import { useUserDetail } from "../../../api/queries/useUserDetail"
import { scrollToFirstInvalidElement } from "../../../common/util"
import { countryItemTemplate, countryValueTemplate } from "../../common/country"

export default function OrderFormLoading() {
  const navigate = useNavigate()
  const { uuid } = useParams<OrderParams>()
  const { data: orderDetail } = useOrderDetail(uuid)
  const { user } = useContext(userContext)
  const { data: userDetail } = useUserDetail(user?.uuid)
  const { data: constants }: { data: ConstantsResponseData | undefined } = useConstants()

  const [isInited, setIsInited] = useState<boolean>(false)
  const [changeCounter, setChangeCounter] = useState<number>(0)
  const [nextStep, setNextStep] = useState<boolean>(false)

  useEffect(() => {
    if (orderDetail && constants) {
      const { loading } = orderDetail.results.orderDetail

      if (loading) {
        setName(loading.name)
        setStreet(loading.street)
        setStreetDetails(loading.streetDetails)
        setZip(loading.zip)
        setCity(loading.city)
        setCountry(loading.country)
        setPerson(loading.person)
        setEmail(loading.email)
        setNotifyPhone(loading.notifyPhone)
        setReference(loading.reference)
        setNote(loading.note)
        setTailgate(loading.tailgate)

        const countryPrefix = constants.results.countries
          .find((country) => country.value === loading.country)?.prefix

        if (loading.phone) {
          const { prefix, number } = splitNumber(constants.results.countries, loading.phone)
          setPhonePrefix(prefix)
          setPhone(number)
        } else {
          if (countryPrefix) {
            setPhonePrefix(countryPrefix)
          }
        }

        if (loading.mobile) {
          const { prefix, number } = splitNumber(constants.results.countries, loading.mobile)
          setMobilePrefix(prefix)
          setMobile(number)
        } else {
          if (countryPrefix) {
            setMobilePrefix(countryPrefix)
          }
        }
      }

      setIsInited(true)
    }
  }, [orderDetail, constants])

  // set default loading address
  useEffect(() => {
    if (userDetail && isInited && orderDetail && constants) {
      const address = userDetail?.results.userDetail.loadingDefaultAddress

      const { loading } = orderDetail.results.orderDetail

      if (
        address &&
        !loading?.name &&
        !loading?.street &&
        !loading?.streetDetails &&
        !loading?.zip &&
        !loading?.city &&
        !loading?.country
      ) {
        setName(address.name)
        setStreet(address.street)
        setStreetDetails(address.streetDetails)
        setZip(address.zip)
        setCity(address.city)
        setCountry(address.country)
        setPerson(address.person)

        if (address.phone) {
          const { prefix, number } = splitNumber(constants.results.countries, address.phone)
          setPhonePrefix(prefix)
          setPhone(number)
        }

        if (address.mobile) {
          const { prefix, number } = splitNumber(constants.results.countries, address.mobile)
          setMobilePrefix(prefix)
          setMobile(number)
        }

        if (address.email) {
          setEmail(address.email)
        }
      }
    }
  }, [userDetail, orderDetail, isInited, constants])

  const updateLoading = useUpdateLoading(uuid)

  const [name, setName] = useState<string>("")
  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
    resetErrorsFor(["name"])
  }

  const [street, setStreet] = useState<string>("")
  const handleStreetChange = (event: ChangeEvent<HTMLInputElement>) => {
    setStreet(event.target.value)
    resetErrorsFor(["street"])
  }

  const [streetDetails, setStreetDetails] = useState<string | null>("")
  const handleStreetDetailsChange = (event: ChangeEvent<HTMLInputElement>) =>
    setStreetDetails(event.target.value)

  const [zip, setZip] = useState<string>("")
  const handleZIPChange = (event: ChangeEvent<HTMLInputElement>) => {
    setZip(event.target.value)
    resetErrorsFor(["zip"])
  }

  const [city, setCity] = useState<string>("")
  const handleCityChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCity(event.target.value)
    resetErrorsFor(["city"])
  }

  const [country, setCountry] = useState<string>("")
  const handleCountryChange = (event: DropdownChangeParams) => {
    setCountry(event.value)
    resetErrorsFor(["country"])

    const prefix = constants?.results.countries
      .find((country) => country.value === event.value)?.prefix

    if (prefix) {
      setPhonePrefix(prefix)
      setMobilePrefix(prefix)
    }
  }

  const [isLoadingDefaultAddress, setIsLoadingDefaultAddress] = useState<boolean>(false)
  const handleIsLoadingDefaultAddressChange = (event: CheckboxChangeParams) =>
    setIsLoadingDefaultAddress(event.checked)

  const [person, setPerson] = useState<string | null>("")
  const handlePersonChange = (event: ChangeEvent<HTMLInputElement>) => setPerson(event.target.value)

  const [phonePrefix, setPhonePrefix] = useState<string>("")
  const [phone, setPhone] = useState<number | null>(null)
  const handlePhoneChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setPhonePrefix(prefix)
    setPhone(value)
    resetErrorsFor(["phone", "mobile"])
  }

  const [mobilePrefix, setMobilePrefix] = useState<string>("")
  const [mobile, setMobile] = useState<number | null>(null)
  const handleMobileChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setMobilePrefix(prefix)
    setMobile(value)
    resetErrorsFor(["mobile", "phone"])
  }

  const [email, setEmail] = useState<string | null>("")
  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)
  const validateEmailBlur = () => setErrors(Object.assign({}, errors, validateEmail(email)))

  const [notifyPhone, setNotifyPhone] = useState<boolean>(false)
  const handleNotifyPhoneChange = (event: CheckboxChangeParams) => setNotifyPhone(event.checked)

  const [reference, setReference] = useState<string | null>("")
  const handleReferenceChange = (event: ChangeEvent<HTMLInputElement>) =>
    setReference(event.target.value)

  const [note, setNote] = useState<string | null>("")
  const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => setNote(event.target.value)

  const [tailgate, setTailgate] = useState<boolean>(false)
  const handleTailgateChange = (event: CheckboxChangeParams) => setTailgate(event.checked)

  const [addressBookUuid, setAddressBookUuid] = useState<string>("")

  const [errors, setErrors] = useState<FormErrors<OrderLoading>>()

  const handleFormSubmit = async (event: SyntheticEvent) => {
    event.preventDefault()

    const payload: UpdateOrderLoadingPaylod = {
      loading: {
        name,
        street,
        streetDetails,
        zip,
        city,
        country,
        person,
        phone: phone ? `${phonePrefix}${phone}` : "",
        mobile: mobile ? `${mobilePrefix}${mobile}` : "",
        email,
        notifyPhone,
        reference,
        note,
        tailgate,
        useAsLoadingDefaultAddress: isLoadingDefaultAddress,
        addressBookUuid: addressBookUuid || null,
      }
    }

    const [isValid, errors] = validateLoading(payload)
    setErrors(errors)

    if (isValid) {
      await updateLoading.mutateAsync(payload)
        .then(() => {
          setChangeCounter(0)
          setNextStep(true)
        })
        .catch(({ response }) => {
          let { data }: { data: UpdateOrderLoadingResponseData } = response
          let respErrors: FormErrors<OrderLoading> = {}

          data.errors.forEach((error) => {
            if (typeof error.property === "string") {
              respErrors[error.property] = error.message
            }
          })

          setErrors(respErrors)
          scrollToFirstInvalidElement()
        })
    }
  }

  const [isAddressBookVisible, setIsAddressBookVisible] = useState<boolean>(false)
  const onAddressBookShow = () => setIsAddressBookVisible(true)
  const onAddressBookHide = () => setIsAddressBookVisible(false)
  const onAddressBookSelect = (address: AddressSimple) => {
    setAddressBookUuid(address.uuid)

    setName(address.name)
    setStreet(address.street)
    setStreetDetails(address.streetDetails)
    setZip(address.zip)
    setCity(address.city)
    setCountry(address.country)
    setPerson(address.person)
    setEmail(address.email)
    setNote(address.note)

    if (!tailgate) {
      setTailgate(address.tailgate)
    }

    if (!notifyPhone) {
      setNotifyPhone(address.notifyPhone)
    }

    if (address.phone) {
      const { prefix, number } = splitNumber(constants?.results.countries || [], address.phone)
      setPhonePrefix(prefix)
      setPhone(number)
    } else {
      setPhonePrefix("")
      setPhone(null)
    }

    if (address.mobile) {
      const { prefix, number } = splitNumber(constants?.results.countries || [], address.mobile)
      setMobilePrefix(prefix)
      setMobile(number)
    } else {
      setMobilePrefix("")
      setMobile(null)
    }

    setIsAddressBookVisible(false)
    resetErrorsFor(["name", "street", "zip", "city", "country", "phone", "mobile"])
  }

  // prompt about unsaved form changes
  useEffect(() => {
    if (isInited) {
      setChangeCounter(i => i + 1)
    }
  }, [isInited, name, street, streetDetails, zip, city, country, person, phone, mobile, email,
    notifyPhone, reference, note, tailgate])

  usePrompt("Máte neuložené změny. Opravdu chcete odejít?", changeCounter > 1)

  // redirect to next step
  useEffect(() => {
    if (changeCounter <= 1 && nextStep) {
      navigate(`/orders/${uuid}/unloading`)
    }
  }, [changeCounter, nextStep, navigate, uuid])

  const isEditable = isOrderEditable(orderDetail?.results.orderDetail.state || 0)

  const resetErrorsFor = (names: string[]) => {
    let obj: { [key: string]: string } = {}

    names.forEach((name) => { obj[name] = "" })

    setErrors(Object.assign({}, errors, obj))
  }

  return (
    <>
      <form className={`col-12 ${isEditable ? "" : "p-disabled"}`} onSubmit={handleFormSubmit}>
        <div className={"p-fluid"}>
          <div className={"border-round-top flex block-title block-title_blue align-items-center px-3"}>
            Místo nakládky

            <Button type={"button"} label={"Adresář"} icon={"pi pi-book"} title={"Adresář"}
              className={"p-button-icon-only p-button-text ml-auto text-color"}
              onClick={onAddressBookShow} />
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={name} onChange={handleNameChange}
                    className={classNames({ "p-invalid": !!errors?.name })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>Název odesílatele:</label>
                </span>
                <FieldError error={errors?.name} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={street} onChange={handleStreetChange}
                    className={classNames({ "p-invalid": !!errors?.street })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>Ulice a číslo domu:</label>
                </span>
                <FieldError error={errors?.street} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={streetDetails || ""} onChange={handleStreetDetailsChange}
                    aria-autocomplete={"none"}
                    className={classNames({ "p-invalid": !!errors?.streetDetails })} />
                  <label>Upřesnění:</label>
                </span>
                <FieldError error={errors?.streetDetails} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={zip} onChange={handleZIPChange}
                    className={classNames({ "p-invalid": !!errors?.zip })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>PSČ:</label>
                </span>
                <FieldError error={errors?.zip} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={city} onChange={handleCityChange}
                    className={classNames({ "p-invalid": !!errors?.city })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>Obec:</label>
                </span>
                <FieldError error={errors?.city} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className="p-float-label">
                  <Dropdown options={constants?.results.countries || []}
                    value={country} onChange={handleCountryChange}
                    className={classNames({ "p-invalid": !!errors?.country })}
                    itemTemplate={countryItemTemplate} valueTemplate={countryValueTemplate} />
                  <label className={commonStyles.labelRequired}>Stát:</label>
                </span>
                <FieldError error={errors?.country} />
              </div>

              <div className={"field col-12"}>
                <div className={"field-checkbox"}>
                  <Checkbox name={"isLoadingDefaultAddress"} inputId={"isLoadingDefaultAddress"}
                    checked={isLoadingDefaultAddress}
                    onChange={handleIsLoadingDefaultAddressChange} />
                  <label htmlFor={"isLoadingDefaultAddress"}>Použít jako výchozí adresu nakládky</label>
                </div>
              </div>
            </div>
          </div>

          <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
            Kontaktní osoba
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <div className={"field-checkbox"}>
                  <Checkbox name={"notifyPhone"} inputId={"notifyPhone"}
                    checked={notifyPhone} onChange={handleNotifyPhoneChange}
                    className={classNames({ "p-invalid": !!errors?.notifyPhone })} />
                  <label htmlFor={"notifyPhone"}>Avizovat nakládku telefonicky</label>
                </div>
                <FieldError error={errors?.notifyPhone} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={person || ""} onChange={handlePersonChange}
                    aria-autocomplete={"none"}
                    className={classNames({ "p-invalid": !!errors?.person })} />
                  <label>Jméno:</label>
                </span>
                <FieldError error={errors?.person} />
              </div>
            </div>

            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <PhoneInput placeholder={"Mobil:"} prefix={mobilePrefix} value={mobile} onChange={handleMobileChange}
                  isRequired={notifyPhone && !phone} error={errors?.mobile} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <PhoneInput placeholder={"Telefon:"} prefix={phonePrefix} value={phone} onChange={handlePhoneChange}
                  isRequired={notifyPhone && !mobile} error={errors?.phone} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={email || ""} onChange={handleEmailChange} onBlur={validateEmailBlur}
                    className={classNames({ "p-invalid": !!errors?.email })}
                    aria-autocomplete={"none"} />
                  <label>E-mail:</label>
                </span>
                <FieldError error={errors?.email} />
              </div>
            </div>
          </div>

          <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
            Důležité informace k nakládce
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText maxLength={30} value={reference || ""} onChange={handleReferenceChange}
                    className={classNames({ "p-invalid": !!errors?.reference })} />
                  <label>Reference k nakládce:</label>
                </span>
                <FieldError error={errors?.reference} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <div className={"field-checkbox"}>
                  <Checkbox name={"tailgate"} inputId={"tailgate"}
                    checked={tailgate} onChange={handleTailgateChange}
                    className={classNames({ "p-invalid": !!errors?.tailgate })} />
                  <label htmlFor={"tailgate"}>Auto s čelem</label>
                </div>
                <FieldError error={errors?.tailgate} />
              </div>

              <div className={"field col-12"}>
                <span className={"p-float-label"}>
                  <InputText maxLength={50} value={note || ""} onChange={handleNoteChange}
                    className={classNames({ "p-invalid": !!errors?.note })} />
                  <label>Dispozice k nakládce:</label>
                </span>
                <FieldError error={errors?.note} />
              </div>
            </div>
          </div>
        </div>

        {isEditable && (
          <div className={`${styles.controls} md:flex`}>
            <span className={"flex-1 text-right"}>
              <Button type={"submit"} label={"Další krok"}
                className={"p-button p-button-sm p-button-success"}
                disabled={updateLoading.isLoading}
                icon={"pi pi-arrow-circle-right"} iconPos={"right"} />
            </span>
          </div>
        )}
      </form>

      <AddressBook isVisible={isAddressBookVisible} onHide={onAddressBookHide}
        onSelect={onAddressBookSelect} />
    </>
  )
}
